"use client"
import React, { useRef, useState } from 'react';
import Link from 'next/link';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRouter } from 'next/navigation';
import Loader from '../components/Loader';

const SignupForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const recaptchaRef2 = useRef<ReCAPTCHA>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [recaptchaValue2, setRecaptchaValue2] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ email?: string; password?: string; privacyAccepted?: string }>({});
  
  const [submitted, setSubmitted] = useState(false);
  const handleRecaptchaChange2 = (value : string | null) => {
    setRecaptchaValue2(value);
  };
  
  const validateForm = () => {
    const newErrors: { email?: string; password?: string; privacyAccepted?: string } = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (!privacyAccepted) {
      newErrors.privacyAccepted = 'You must accept the privacy policy and terms of service';
    }
    if (!recaptchaValue2) {
      return;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setLoading(true);
    if (validateForm()) {
      // Simulating an API call with a placeholder JSON API
      try {
        const response = await fetch('/api/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
            privacyAccepted,
            recaptcha_token: recaptchaValue2,
          }),
        });
        
        const data = await response.json();
        if(data.success == true){
          toast.success(data.message);
          setEmail('');
          setPassword('');
          setRecaptchaValue2('');
          setPrivacyAccepted(false);
          setSubmitted(false);
          if (data.success && data.redirectUrl) {
            router.push(data.redirectUrl);
          }
        }else{
          setRecaptchaValue2('');
          recaptchaRef2.current?.reset();
          toast.error(data.message);
        }
          
      } catch (error) {
        setRecaptchaValue2('');
        recaptchaRef2.current?.reset();
        const message = error instanceof Error ? error.message : String(error);
        toast.error(message);
      }
      
      setLoading(false);
    }else{
      setLoading(false);
    }
  };

  // Function to clear error for the specific field when the user starts typing
  const handleInputChange = (field: string, value: string) => {
    if (submitted) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: '',
      }));
    }
    if (field === 'email') setEmail(value);
    if (field === 'password') setPassword(value);
  };

  return (
    <>
      {loading && <Loader title="Please wait… we’re redirecting you to awesomeness!" />}
      <div className="loginForm0">
        <form method="post" action="/adduser" name="createaccount" id="createaccount" onSubmit={handleSubmit}>
          <input type="hidden" name="_token" value="OsrLJoeT1egzpaioSEvESzJStuBLFBMtkXax8y54" />
          <h4 className='textHad h2 text-center fw-bold mb-2'>Signup to Start Your Journey</h4>
          
          {/* Email input */}
          <div className="my-4">
          <h6 className='textHad fs-6 fw-bold'>Email</h6>
            <div className="input-group input-group-lg p-0 rounded-3 custInput">
              
              <input
                type="email"
                name="email"
                className="form-control"
                placeholder="Enter your email"
                aria-label="Enter your email"
                aria-describedby="inputGroup-sizing-lg"
                value={email}
                onChange={(e) => handleInputChange('email', e.target.value)} // Handle change
              />
            </div>
            {errors.email && submitted && <div className="text-danger mt-2">{errors.email}</div>}
          </div>

          {/* Password input */}
          <div className="my-4">
          <h6 className='textHad fs-6 fw-bold'>Password</h6>
            <div className="input-group input-group-lg p-0 rounded-3 custInput">
              <input
                name="password"
                type="password"
                className="form-control"
                placeholder="Create your password"
                id="inputPassword"
                autoComplete="off"
                aria-label="Create your password"
                value={password}
                onChange={(e) => handleInputChange('password', e.target.value)} // Handle change
              />
            </div>
            {errors.password && submitted && <div className="text-danger mt-2">{errors.password}</div>}
          </div>

          {/* Privacy Policy Checkbox */}
          <div className="mb-3"> 
            <div className="form-check m-0  p-0" style={{ width: '100%', border: 'none', textAlign: 'left' }}>
              <input
                className="form-check-input"
                type="checkbox"
                style={{ marginLeft: '0px', marginRight: '5px' }}
                value=""
                id="flexCheckChecked"
                name="privacyaccpt"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
              />
              <label className="form-check-label text-left custText textPara" htmlFor="flexCheckChecked">
                I have read and agree with <Link href="/privacy-policy" className='text-decoration-none textOrange'>Privacy Policy </Link>  and <Link href="/terms-of-use" className='text-decoration-none textOrange'>Terms Of Service</Link>
              </label>
            </div>
            {errors.privacyAccepted && submitted && <div className="text-danger mt-2">{errors.privacyAccepted}</div>}
          </div>

          <div className="my-4">
            <ReCAPTCHA
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
              onChange={handleRecaptchaChange2}
              ref={recaptchaRef2}
            />
          </div>
          
          {!recaptchaValue2 && submitted && <div className="my-4 d-flex"><div className="text-danger mt-2">Please verify that you are not a robot.</div></div>}
          <input type="hidden" name="recaptcha_token2" id="recaptcha_token2" value={recaptchaValue2 || ''} />
          
          <div className="d-grid gap-2 mb-3">
            <button className="btn btn-primary btnfill0" type="submit">
              Create Account
            </button> 
          </div>
        </form>

        {/* Toast Container */}
        <ToastContainer /> {/* Corrected component */}
      </div>
    </>
  );
};

export default SignupForm;
